import { DestroyRef, inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, fromEvent, interval, merge, switchMap, tap } from 'rxjs';
import { CurrentUser } from '../models/user';
import { map } from 'rxjs/operators';
import { PrimeNG } from 'primeng/config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - this is only an issue within WebStorm, who somehow does not correctly recognize tsconfig.app.json
const tbLang = window.__tb_lang;

/**
 * @class TranslationManagerService
 * This service provides functionalities for managing the translations: initialization, reset, etc...
 *
 * @see See also assets/js/lang.js for language bootstrapping, that has to be executed before Angular is loaded
 */
@Injectable({ providedIn: 'root' })
export class TranslationManagerService {
    private readonly DEBUG_MODE = true;
    private readonly BROWSER_LANG_CHANGE_DETECTION_INTERVAL = 2000; // ms
    private isUserDefinedLang: boolean = false;
    private lastBrowserLang: string | undefined;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly translateService: TranslateService,
        private readonly primeng: PrimeNG,
    ) {
        this.lastBrowserLang = tbLang.getBrowserLang();
        this.translateService.onLangChange
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => this.translateService.get('primeng')),
                tap((primengTranslations) => {
                    this.debugLog('PrimeNG translations loaded');
                    this.primeng.setTranslation(primengTranslations);
                }),
            )
            .subscribe();
    }

    private debugLog(msg: string): void {
        if (this.DEBUG_MODE) {
            console.debug('TranslationManagerService: ' + msg);
        }
    }

    public initTranslations(): void {
        this.debugLog('init called');
        this.translateService.addLangs(tbLang.supportedLanguages);
        this.setTranslationsToBrowserLang();
        this.registerBrowserLangWatcher();
    }

    public setTranslationsToBrowserLang(): void {
        const browserLang = tbLang.getBrowserLang();
        const supportedLang = tbLang.getSupportedLanguage(browserLang);
        this.translateService.setFallbackLang(supportedLang);
        this.useLang(supportedLang, browserLang);
        this.lastBrowserLang = browserLang;
        this.isUserDefinedLang = false;
        this.debugLog('Now using browser language: ' + browserLang);
    }

    private registerBrowserLangWatcher(): void {
        merge(
            fromEvent(window, 'languagechange'),
            interval(this.BROWSER_LANG_CHANGE_DETECTION_INTERVAL).pipe(
                map(() => navigator.languages[0] || navigator.language),
            ),
        )
            .pipe(
                debounceTime(500),
                distinctUntilChanged(), // TODO distinctUntilChanged() handles both triggers separately resulting in an extra trigger call
                tap((browserLang: string | Event) => {
                    this.debugLog('Browser lang change event trigger: ' + browserLang.toString());
                    this.onBrowserLanguageChange();
                }),
            )
            .subscribe();
    }

    private onBrowserLanguageChange(): void {
        const browserLang = tbLang.getBrowserLang();
        const supportedLang = tbLang.getSupportedLanguage(browserLang);
        if (browserLang === this.lastBrowserLang) {
            this.debugLog(
                `Language change triggered (${browserLang}) without a change in resulting browser language. Skipping change.`,
            );
            return;
        }
        let debugMsg = 'Change of browser language detected. Changed fallback ';
        this.translateService.setFallbackLang(supportedLang);
        if (!this.isUserDefinedLang) {
            this.useLang(supportedLang, browserLang);
            debugMsg += 'and active ';
        }
        this.debugLog(debugMsg + 'language to: ' + browserLang);
        this.lastBrowserLang = browserLang;
    }

    /**
     * Sets the language of the application to the language of the user.
     * If the user has no language setting, the fallback language is used.
     * It will only emit a language change if the language actually changed.
     * @param user The user to get the language from.
     */
    public setUserLanguage(user: CurrentUser): void {
        const oldVal = this.isUserDefinedLang;
        this.isUserDefinedLang = !!user.settings?.language;
        const userDefinedLangChanged = oldVal !== this.isUserDefinedLang;

        let lang = user.settings?.language
            ? user.settings?.language
            : (this.translateService.getFallbackLang() ?? tbLang.defaultLanguage);

        // in case the user's language setting is invalid (e.g., when language was removed) use the default language:
        if (!tbLang.supportedLanguages.includes(lang)) {
            console.error(
                `User language "${lang}" is not supported. Falling back to default language "${tbLang.defaultLanguage}"`,
            );
            lang = tbLang.defaultLanguage;
        }

        // avoid unnecessary language change operations:
        if (!userDefinedLangChanged && lang === this.translateService.getCurrentLang()) {
            this.debugLog('User language unchanged. Skipping change.');
            return;
        }

        this.useLang(lang);
        if (this.isUserDefinedLang) {
            this.debugLog('Now using user language: ' + lang);
        }
    }

    private useLang(lang: string, browserLang: string | undefined = this.lastBrowserLang): void {
        this.translateService.use(lang);
        try {
            tbLang.modifyHtmlLang(lang, browserLang);
            tbLang.setLocalStorageLang(lang);
        } catch (error) {
            console.error(error);
        }
    }
}
